/* Tonic Home V2 header: rotating top bar messages, search suggestions and the phone search toggle.
   The search results themselves come from the theme's predictive-search element. */
class ThHeader extends HTMLElement {
  connectedCallback() {
    this.setHeight = () => document.documentElement.style.setProperty('--header-height', `${this.offsetHeight}px`);
    this.setHeight();
    if ('ResizeObserver' in window) new ResizeObserver(this.setHeight).observe(this);

    this.initMessages();
    this.initSearch();
    this.initMenus();
    this.initDrawer();
    this.initCondense();
  }

  /* Smaller header after scrolling. Two thresholds, so the shrinking header can't flip it back. */
  initCondense() {
    let ticking = false;
    const update = () => {
      ticking = false;
      const y = window.scrollY;
      if (!this.classList.contains('is-condensed') && y > 120) this.classList.add('is-condensed');
      else if (this.classList.contains('is-condensed') && y < 40) this.classList.remove('is-condensed');
    };
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* Phone and tablet menu: slides in from the left, with a panel per top item. */
  initDrawer() {
    this.drawer = this.querySelector('.th-drawer');
    this.burger = this.querySelector('.th-burger');
    this.scrim = this.querySelector('.th-scrim');
    if (!this.drawer || !this.burger) return;
    this.burger.addEventListener('click', () => this.openDrawer());
    this.drawer.querySelector('.th-drawer__close').addEventListener('click', () => this.closeDrawer());
    this.scrim.addEventListener('click', () => this.closeDrawer());
    this.drawer.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (this.drawer.querySelector('.localization-form__select[aria-expanded="true"]')) return;
        return this.drawer.classList.contains('is-sub') ? this.showRoot() : this.closeDrawer();
      }
      if (event.key !== 'Tab') return;
      // Keep keyboard focus inside the open menu.
      const focusable = [...this.drawer.querySelectorAll('a[href], button, input, [tabindex]:not([tabindex="-1"])')]
        .filter((el) => el.offsetParent !== null && !el.closest('[hidden]'));
      if (!focusable.length) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) { event.preventDefault(); last.focus(); }
      else if (!event.shiftKey && document.activeElement === last) { event.preventDefault(); first.focus(); }
    });
    this.drawer.querySelectorAll('[data-sub]').forEach((btn) => btn.addEventListener('click', () => this.showSub(btn)));
    this.drawer.querySelectorAll('.th-dback').forEach((btn) => btn.addEventListener('click', () => this.showRoot()));
    this.drawer.querySelectorAll('.th-acc__btn').forEach((btn) => btn.addEventListener('click', () => {
      const acc = btn.closest('.th-acc');
      const open = !acc.classList.contains('is-open');
      acc.classList.toggle('is-open', open);
      btn.setAttribute('aria-expanded', open);
    }));
    matchMedia('(min-width: 990px)').addEventListener('change', (mq) => { if (mq.matches) this.closeDrawer(false); });
  }

  openDrawer() {
    this.closePane && this.closePane();
    this.drawer.hidden = false;
    void this.drawer.offsetWidth; // Lets the slide-in transition run after unhiding.
    this.drawer.classList.add('is-open');
    this.scrim.classList.add('is-on');
    this.burger.setAttribute('aria-expanded', 'true');
    document.documentElement.classList.add('th-locked');
    this.drawer.querySelector('.th-drawer__close').focus({ preventScroll: true });
  }

  closeDrawer(returnFocus = true) {
    if (!this.drawer || this.drawer.hidden) return;
    this.drawer.classList.remove('is-open');
    this.scrim.classList.remove('is-on');
    this.burger.setAttribute('aria-expanded', 'false');
    document.documentElement.classList.remove('th-locked');
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    setTimeout(() => { this.drawer.hidden = true; this.showRoot(false); }, reduce ? 0 : 450);
    if (returnFocus) this.burger.focus({ preventScroll: true });
  }

  showSub(btn) {
    const sub = this.drawer.querySelector(`#${btn.dataset.sub}`);
    if (!sub) return;
    this.drawer.querySelectorAll('.th-dpanel--sub').forEach((p) => { p.hidden = p !== sub; });
    sub.scrollTop = 0;
    this.drawer.classList.add('is-sub');
    btn.setAttribute('aria-expanded', 'true');
    this.activeSubButton = btn;
    sub.querySelector('.th-dback').focus({ preventScroll: true });
  }

  showRoot(returnFocus = true) {
    this.drawer.classList.remove('is-sub');
    this.drawer.querySelectorAll('.th-dpanel--sub').forEach((p) => { p.hidden = true; });
    if (this.activeSubButton) {
      this.activeSubButton.setAttribute('aria-expanded', 'false');
      if (returnFocus) this.activeSubButton.focus({ preventScroll: true });
      this.activeSubButton = null;
    }
  }

  /* Desktop hover menus. Hover opens after a short pause and closes once the pointer has left
     both the nav row and the open menu; keyboard focus opens too, and Escape closes. */
  initMenus() {
    this.nav = this.querySelector('.th-nav');
    if (!this.nav) return;
    this.items = [...this.nav.querySelectorAll('.th-nav__item.has-pane')];
    this.indicator = this.nav.querySelector('.th-nav__indicator');
    this.backdrop = this.querySelector('.th-backdrop');
    this.desktop = matchMedia('(min-width: 990px)');
    this.canHover = matchMedia('(hover: hover)');
    let openTimer = 0;
    let closeTimer = 0;
    const later = (fn, ms) => { clearTimeout(openTimer); clearTimeout(closeTimer); return setTimeout(fn, ms); };
    // Menu images are lazy, so they would only start loading when a menu first opens. Start them
    // when the pointer or focus first reaches the nav row, so the tiles are ready on open.
    const warm = () => this.nav.querySelectorAll('.th-mega img[loading="lazy"]').forEach((img) => { img.loading = 'eager'; });
    this.nav.addEventListener('mouseenter', warm, { once: true });
    this.nav.addEventListener('focusin', warm, { once: true });

    this.items.forEach((item) => {
      const link = item.querySelector('.th-nav__link');
      item.addEventListener('mouseenter', () => {
        if (!this.desktop.matches) return;
        openTimer = later(() => this.openPane(item), this.openItem ? 0 : 90);
      });
      link.addEventListener('focus', () => { if (this.desktop.matches) this.openPane(item); });
      // Touch screens at desktop width: the first tap opens the menu, the second follows the link.
      link.addEventListener('click', (event) => {
        if (this.desktop.matches && !this.canHover.matches && this.openItem !== item) {
          event.preventDefault();
          this.openPane(item);
        }
      });
    });
    this.nav.querySelectorAll('.th-nav__item:not(.has-pane)').forEach((item) => {
      item.addEventListener('mouseenter', () => { closeTimer = later(() => this.closePane(), 120); });
      item.querySelector('a').addEventListener('focus', () => this.closePane());
    });
    this.nav.addEventListener('mouseleave', () => { closeTimer = later(() => this.closePane(), 180); });
    this.nav.addEventListener('mouseenter', () => clearTimeout(closeTimer));
    this.nav.addEventListener('keydown', (event) => {
      if (event.key !== 'Escape' || !this.openItem) return;
      const link = this.openItem.querySelector('.th-nav__link');
      this.closePane();
      link.focus({ preventScroll: true });
    });
    this.nav.addEventListener('focusout', (event) => {
      if (!this.nav.contains(event.relatedTarget)) this.closePane();
    });
    if (this.backdrop) this.backdrop.addEventListener('click', () => this.closePane());
    this.desktop.addEventListener('change', () => this.closePane());

    // Shop: the side panel follows the hovered or focused category.
    this.nav.querySelectorAll('.th-catlist').forEach((list) => {
      const pane = list.closest('.th-pane');
      const show = (cat) => {
        pane.querySelectorAll('.th-cat').forEach((c) => c.classList.toggle('is-current', c === cat));
        pane.querySelectorAll('.th-flyout').forEach((f) => f.classList.toggle('is-active', f.dataset.cat === cat.dataset.cat));
      };
      if (!pane.querySelector('.th-cat.is-current')) show(pane.querySelector('.th-cat'));
      list.querySelectorAll('.th-cat').forEach((cat) => {
        cat.addEventListener('mouseenter', () => show(cat));
        cat.addEventListener('focus', () => show(cat));
      });
    });

    // Shop: colour name next to the swatches.
    this.nav.querySelectorAll('.th-colours').forEach((row) => {
      const label = row.querySelector('.th-swatch-name');
      row.querySelectorAll('.th-swatch').forEach((sw) => {
        const name = () => { label.textContent = sw.dataset.name; };
        sw.addEventListener('mouseenter', name);
        sw.addEventListener('focus', name);
      });
      row.addEventListener('mouseleave', () => { label.textContent = ''; });
    });
  }

  openPane(item) {
    if (this.openItem === item) return;
    this.closePane(false);
    const pane = this.querySelector(`#${item.dataset.pane}`);
    if (!pane) return;
    if (pane.classList.contains('th-pane--mini')) {
      const inner = this.nav.querySelector('.th-mega').getBoundingClientRect();
      pane.style.setProperty('--th-x', `${Math.round(item.getBoundingClientRect().left - inner.left)}px`);
    }
    pane.hidden = false;
    item.classList.add('is-open');
    item.querySelector('.th-nav__link').setAttribute('aria-expanded', 'true');
    this.openItem = item;
    if (this.backdrop) this.backdrop.classList.add('is-on');
    if (this.indicator) {
      const box = item.getBoundingClientRect();
      const parent = this.indicator.parentElement.getBoundingClientRect();
      this.indicator.style.left = `${box.left - parent.left + 18}px`;
      this.indicator.style.width = `${box.width - 36}px`;
      this.indicator.classList.add('is-on');
    }
  }

  closePane(all = true) {
    if (!this.openItem) return;
    const item = this.openItem;
    const pane = this.querySelector(`#${item.dataset.pane}`);
    if (pane) pane.hidden = true;
    item.classList.remove('is-open');
    item.querySelector('.th-nav__link').setAttribute('aria-expanded', 'false');
    this.openItem = null;
    if (all) {
      if (this.backdrop) this.backdrop.classList.remove('is-on');
      if (this.indicator) this.indicator.classList.remove('is-on');
    }
  }

  initMessages() {
    this.track = this.querySelector('.th-top__track');
    this.msgs = this.track ? [...this.track.children] : [];
    this.dots = [...this.querySelectorAll('.th-top__dots i')];
    if (this.msgs.length < 2) return;
    this.current = 0;
    const seconds = parseInt(this.dataset.rotate, 10) || 5;
    const area = this.querySelector('.th-top__msgs');
    let paused = false;
    area.addEventListener('mouseenter', () => { paused = true; });
    area.addEventListener('mouseleave', () => { paused = false; });
    area.addEventListener('focusin', () => { paused = true; });
    area.addEventListener('focusout', () => { paused = false; });
    setInterval(() => {
      if (!paused && !document.hidden) this.showMessage((this.current + 1) % this.msgs.length);
    }, seconds * 1000);
  }

  showMessage(index) {
    this.current = index;
    this.track.style.transform = `translateY(-${index * this.msgs[0].offsetHeight}px)`;
    this.msgs.forEach((msg, i) => msg.setAttribute('aria-hidden', i !== index));
    this.dots.forEach((dot, i) => dot.classList.toggle('is-on', i === index));
  }

  initSearch() {
    this.search = this.querySelector('.th-search');
    if (!this.search) return;
    this.input = this.search.querySelector('input[type="search"]');
    this.suggest = this.search.querySelector('.th-suggest');
    const update = () => {
      if (!this.suggest) return;
      this.suggest.hidden = !(this.search.contains(document.activeElement) && this.input.value.trim() === '');
    };
    this.input.addEventListener('focus', update);
    this.input.addEventListener('input', update);
    this.search.addEventListener('focusout', () => setTimeout(update, 0));
    this.search.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        if (this.suggest) this.suggest.hidden = true;
        this.input.blur();
      }
    });

    this.toggle = this.querySelector('.th-util--search');
    if (this.toggle) this.toggle.addEventListener('click', () => {
      const open = !this.classList.contains('is-search-open');
      this.classList.toggle('is-search-open', open);
      this.toggle.setAttribute('aria-expanded', open);
      if (open) this.input.focus();
    });
  }
}

if (!customElements.get('th-header')) customElements.define('th-header', ThHeader);
